import { useEffect, useRef, useSyncExternalStore } from "react";
import { io, Socket } from "socket.io-client";
import { useAuth } from "./use-auth";
import { useQueryClient } from "@tanstack/react-query";
import {
  getGetNotificationCountsQueryKey,
  getListMatchesQueryKey,
  getListMessagesQueryKey,
  type Match,
  type Message,
  type MessagesResponse,
  type NotificationCounts,
} from "@workspace/api-client-react";

let globalSocket: Socket | null = null;
const socketStateListeners = new Set<() => void>();

function notifySocketState(): void {
  for (const listener of socketStateListeners) listener();
}

export function useSocketConnection(): { socket: Socket | null; connected: boolean } {
  const snapshot = useSyncExternalStore(
    (listener) => {
      socketStateListeners.add(listener);
      return () => socketStateListeners.delete(listener);
    },
    () => `${globalSocket ? "ready" : "missing"}:${globalSocket?.connected === true}`,
    () => "missing:false",
  );
  return {
    socket: globalSocket,
    connected: snapshot.endsWith(":true"),
  };
}

export function useSocket() {
  const { isAuthenticated, user } = useAuth();
  const queryClient = useQueryClient();
  const connectedRef = useRef(false);

  useEffect(() => {
    if (!isAuthenticated) {
      if (globalSocket) {
        globalSocket.disconnect();
        globalSocket = null;
        connectedRef.current = false;
        notifySocketState();
      }
      return;
    }

    if (connectedRef.current && globalSocket?.connected) return;

    const basePath = import.meta.env.BASE_URL.replace(/\/$/, "");
    globalSocket = io({
      path: `${basePath}/api/socket.io`,
      withCredentials: true,
      transports: ["websocket", "polling"],
    });
    notifySocketState();

    globalSocket.on("connect", () => {
      connectedRef.current = true;
      notifySocketState();
      queryClient.invalidateQueries({ queryKey: getListMatchesQueryKey() });
      queryClient.invalidateQueries({ queryKey: getGetNotificationCountsQueryKey() });
    });

    globalSocket.on("new-message", (data: Message) => {
      if (!data?.matchId) return;
      const viewerMessage = { ...data, isMine: data.senderId === user?.id };
      queryClient.setQueryData<MessagesResponse>(
        getListMessagesQueryKey(data.matchId),
        (old) => {
          if (!old || old.messages.some((message) => message.id === data.id)) return old;
          return { ...old, messages: [...old.messages, viewerMessage] };
        },
      );
    });

    globalSocket.on("conversation-update", (data: {
      matchId: string;
      lastMessage: string;
      lastMessageAt: string;
    }) => {
      queryClient.setQueryData<Match[]>(getListMatchesQueryKey(), (old) => {
        if (!old) return old;
        const updated = old.map((match) => match.id === data.matchId
          ? {
              ...match,
              lastMessage: data.lastMessage,
              lastMessageAt: data.lastMessageAt,
              unreadCount: match.unreadCount + 1,
            }
          : match);
        return updated.sort((a, b) => {
          const aTime = a.lastMessageAt || a.matchedAt || a.createdAt;
          const bTime = b.lastMessageAt || b.matchedAt || b.createdAt;
          return new Date(bTime).getTime() - new Date(aTime).getTime();
        });
      });
      queryClient.setQueryData<NotificationCounts>(
        getGetNotificationCountsQueryKey(),
        (old) => old ? { ...old, unreadMessages: old.unreadMessages + 1 } : old,
      );
    });

    globalSocket.on("messages-read", (data: { matchId?: string; readByUserId?: string }) => {
      if (!data?.matchId || !data.readByUserId) return;
      const readAt = new Date().toISOString();
      queryClient.setQueryData<MessagesResponse>(
        getListMessagesQueryKey(data.matchId),
        (old) => old ? {
          ...old,
          messages: old.messages.map((message) => {
            const wasReadByCurrentUser = data.readByUserId === user?.id;
            const shouldMarkRead = wasReadByCurrentUser ? !message.isMine : message.isMine;
            return shouldMarkRead && !message.readAt ? { ...message, readAt } : message;
          }),
        } : old,
      );
      if (data.readByUserId === user?.id) {
        queryClient.setQueryData<Match[]>(getListMatchesQueryKey(), (old) =>
          old?.map((match) => match.id === data.matchId ? { ...match, unreadCount: 0 } : match),
        );
        queryClient.invalidateQueries({ queryKey: getGetNotificationCountsQueryKey() });
      }
    });

    globalSocket.on("disconnect", () => {
      connectedRef.current = false;
      notifySocketState();
    });

    return () => {
      if (globalSocket) {
        globalSocket.disconnect();
        globalSocket = null;
        connectedRef.current = false;
        notifySocketState();
      }
    };
  }, [isAuthenticated, queryClient, user?.id]);

  return globalSocket;
}

export function getSocket(): Socket | null {
  return globalSocket;
}
